import { useState, useEffect, useCallback, useMemo } from "react";
import { API, graphqlOperation } from "aws-amplify";
import * as mutations from "../../graphql/mutations";
import { useNavigate, useParams } from "react-router-dom";
import "./EditPolicy.css";
import { InputForm } from "../InputForm/InputForm";
import { Button, Container } from "react-bootstrap";
import { Checkbox } from "../Checkbox/Checkbox";
import { getPolicyById } from "../../graphql/queries";
import { PolicyType } from "../PrivacyPolicy/PrivacyPolicType";
import { getPolicyByIdType, selectStyles } from "./EditPolicyTypes";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { Notification } from "../Notification/Notification";
import { Modal } from "../Modal/Modal";
import { Heading, useAuthenticator } from "@aws-amplify/ui-react";
import Select from "react-select";
import {
  MAX_BODY_LENGTH,
  MAX_HEADER_LENGTH,
} from "../NewPrivacyPolicy/Constants";
import { BackButton } from "../_shared/Buttons/BackButton";

export const EditPolicy = () => {
  const { signOut } = useAuthenticator();
  const [policy, setPolicy] = useState({} as PolicyType);
  const [defaultLabel, setDefaultLabel] = useState("");
  const { id } = useParams();
  const [policyLoaded, setPolicyLoaded] = useState(false);
  const [toastTitle, setToastTitle] = useState("" as string);
  const [toastDescription, setToastDescription] = useState("" as string);
  const [showToast, setShowToast] = useState(false as boolean);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  type OptionType = {
    value: string;
    label: string;
  };

  const options: OptionType[] = useMemo(
    () => [
      { value: "ti", label: "Travel Insurance" },
      { value: "tmc", label: "Travel Money Card" },
      { value: "app", label: "Travel App" },
    ],
    []
  );

  const selectLabel = useCallback(
    (policyType: string) => {
      if (policyType === "ti") {
        setDefaultLabel(options[0].label);
      }
      if (policyType === "tmc") {
        setDefaultLabel(options[1].label);
      }
      if (policyType === "app") {
        setDefaultLabel(options[2].label);
        console.log("setting label");
      }
    },
    [options]
  );

  const handlePolicy = useCallback(async () => {
    try {
      const policyById = (await API.graphql(
        graphqlOperation(getPolicyById, {
          policyId: id,
        })
      )) as GraphQLResult<getPolicyByIdType>;
      console.log(policyById);
      const policyData = policyById?.data?.getPolicyById;
      selectLabel(policyById?.data?.getPolicyById.policyType);
      setPolicy(policyData);
      setPolicyLoaded(true);
    } catch (e) {
      console.log(e);
    }
  }, [id, selectLabel]);

  const handleChangeText = (value: string, name: string) => {
    setPolicy((policy) => {
      return {
        ...policy,
        [name]: value,
      };
    });
  };

  const handleDescription = (event) => {
    let newBody = event.target.value;
    setPolicy((policy) => {
      return {
        ...policy,
        body: newBody,
      };
    });
  };

  const handleSelectChange = (selected) => {
    setPolicy((policy) => {
      return {
        ...policy,
        policyType: selected.value,
      };
    });
  };

  const handleOpenDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleOpenEditModal = () => {
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleCheckValue = (event: React.MouseEvent<HTMLInputElement>) => {
    const currentTarget = event.target as HTMLInputElement;
    const checked = currentTarget.checked;
    const name = currentTarget.name;
    setPolicy((policy) => {
      return {
        ...policy,
        [name]: checked,
      };
    });
  };

  const handleNotificationClose = () => {
    setShowToast(false);
  };

  const navigate = useNavigate();

  const goBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    handlePolicy();
  }, [handlePolicy]);

  const disableButton = (): boolean => {
    const { header, body, policyType } = policy;
    if (
      header?.length === 0 ||
      header?.length > MAX_HEADER_LENGTH ||
      body?.length === 0 ||
      body?.length > MAX_BODY_LENGTH ||
      policyType?.length === 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleUpdatePolicy = async () => {
    try {
      console.log("Policy update: " + JSON.stringify(policy));
      await API.graphql(
        graphqlOperation(mutations.updatePolicy, {
          policy,
        })
      );
      console.log("Updated policy");
      setToastTitle("Policy Successfully Updated");
      setToastDescription(
        `Policy with header ${policy.header} has been updated successfully`
      );
      setShowEditModal(false);
      setShowToast(true);
    } catch (e) {
      console.log(e);
      signOut();
    }
  };

  const handleDeletePolicy = async (id: string) => {
    try {
      await API.graphql(
        graphqlOperation(mutations.deletePolicy, {
          policyId: id,
        })
      );
      setShowDeleteModal(false);
      setShowToast(true);
      setToastTitle("Policy Deleted");
      setToastDescription(
        `Policy with header ${policy.header} has been deleted`
      );
      const navTimer = setTimeout(() => {
        navigate(-1);
      }, 4000);
      return () => clearTimeout(navTimer);
    } catch (e) {
      console.log(e);
      signOut();
    }
  };

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/policy");
  };

  const toastVariant = toastTitle === "Error!" ? "danger" : "success";

  return (
    <Container fluid as={"div"} className="screenContainer">
      <div className="pushCard">
        <BackButton onClick={onClick} label={"Back to policies"} />
        <Heading level={2} className="screen-title">
          Edit existing policy
        </Heading>
        {policyLoaded ? (
          <div className="formContainer">
            <div className="formItem">
              <InputForm
                name="header"
                ariaLabel="Title"
                size="lg"
                inLineLabel
                onChange={handleChangeText}
                labelText="Policy Header"
                controlId="formMessages"
                defaultValue={policy?.header}
              />
              {policy?.header?.length > 0 && (
                <div className="row-text">
                  <h6
                    className={
                      policy?.header?.length > MAX_HEADER_LENGTH
                        ? "errorText"
                        : "validText"
                    }
                  >
                    {policy?.header?.length}
                  </h6>
                  <h6 className="validText">{` /${MAX_HEADER_LENGTH}`}</h6>
                </div>
              )}
            </div>
            <div className="formItem field-row">
              <div className="description-title">Select Policy Type</div>
              <Select
                defaultValue={{
                  value: policy.policyType,
                  label: defaultLabel,
                }}
                styles={selectStyles}
                options={options}
                onChange={handleSelectChange}
              />
            </div>
            <div className="formItem field-row">
              <div className="description-title desc">Policy Body</div>
              <textarea
                value={policy.body}
                className="description"
                onChange={handleDescription}
                defaultValue={policy?.body}
              />
            </div>
            {policy?.body?.length > 0 && (
              <div className="row-text">
                <h6
                  className={
                    policy?.body?.length > MAX_BODY_LENGTH
                      ? "errorText"
                      : "validText"
                  }
                >
                  {policy?.body?.length}
                </h6>
                <h6 className="validText">{` /${MAX_BODY_LENGTH}`}</h6>
              </div>
            )}
            <div className="formItem">
              <span className="checkbox-item">
                <Checkbox
                  label="Is Active"
                  name="isActive"
                  handleCheck={handleCheckValue}
                  defaultChecked={policy?.isActive}
                  className="action-checkbox"
                />
              </span>
            </div>
            <div className="formItem formActionsRight">
              <Button
                type="button"
                variant="primary"
                className="btn btn-primary"
                disabled={disableButton()}
                onClick={() => handleOpenEditModal()}
              >
                Save
              </Button>
              <Button
                type="button"
                variant="danger"
                className="btn btn-danger"
                onClick={() => handleOpenDeleteModal()}
              >
                Delete
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                className="btn btn-outline-secondary"
                onClick={goBack}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="notificationContainer">
        <Notification
          show={showToast}
          timer={50000}
          position="top-center"
          title={toastTitle}
          description={toastDescription}
          autohide={true}
          handleClose={handleNotificationClose}
          variant={toastVariant}
        />
      </div>
      <Modal
        enableModal={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleSubmit={() => handleDeletePolicy(id)}
        title={"WARNING"}
        closeButtonText="Back"
        actionButtonText="Delete"
        width="halfwidth"
      >
        Are you sure you would like to delete this policy? Once it has been
        deleted this cannot be reversed
      </Modal>
      <Modal
        enableModal={showEditModal}
        handleClose={handleCloseEditModal}
        handleSubmit={() => handleUpdatePolicy()}
        title={"Editing Policy"}
        closeButtonText="Back"
        actionButtonText="Edit"
        width="halfwidth"
      >
        Are you sure you would like to edit this policy?
      </Modal>
    </Container>
  );
};
